import {
  useFlow,
  usePersistedSessionState,
} from "@edreamsodigeo/frontend-core";

import useMembershipProductQuery from "~/clientState/hooks/useMembershipProductQuery";
import { usePrimePriceSelectedInResults } from "~/clientState/hooks/usePrimePriceSelectedInResults";
import useShoppingBundleIsPerksApplied from "~/clientState/hooks/useShoppingBundleIsPerksApplied";
import useUserAccount from "~/clientState/hooks/useUserAccount";
import { isMembershipActivatedWithUserAccountData } from "~/clientState/hooks/utils/MembershipUtils";
import { useSubscriptionActionTaken } from "~/clientState/providers/SubscriptionActionTakenProvider";
import { FeeSelected } from "~/generated/graphql";

export interface UseFeeSelected {
  feeSelected: FeeSelected;
}

const useFeeSelectedPaymentMobile = ({
  isDiscountAppliedInShoppingBundle,
}): UseFeeSelected => {
  return {
    feeSelected: isDiscountAppliedInShoppingBundle
      ? FeeSelected.PrimePrice
      : FeeSelected.FullPrice,
  };
};

export const useFeeSelected = (): UseFeeSelected => {
  const { data: membershipData } = useUserAccount();
  const { data: membershipProductData } = useMembershipProductQuery();
  const isDiscountAppliedInShoppingBundle = useShoppingBundleIsPerksApplied();

  const wasPrimePriceSelectedInResult = usePrimePriceSelectedInResults();
  const { wasActionTaken } = useSubscriptionActionTaken();
  const [subscriptionSelectedOnContinue] = usePersistedSessionState(
    "subscriptionSelectedOnContinue",
  );

  const isPrimeFareSelectedOnResultsOrPax =
    subscriptionSelectedOnContinue === "prime";
  const wasPrimePriceSelected =
    wasPrimePriceSelectedInResult || isPrimeFareSelectedOnResultsOrPax;
  const isPrimeAdded = !!membershipProductData?.membershipProduct?.offerAdded;
  const isPrimeMember =
    isMembershipActivatedWithUserAccountData(membershipData) &&
    isDiscountAppliedInShoppingBundle;
  const flow = useFlow();
  const feeSelectedMobile = useFeeSelectedPaymentMobile({
    isDiscountAppliedInShoppingBundle,
  });
  const feeSelectedDesktop = useFeeSelectedDesktop({
    isPrimeMember,
    isPrimeAdded,
    wasPrimePriceSelected,
    wasActionTaken,
  });
  return flow === "mobile" ? feeSelectedMobile : feeSelectedDesktop;
};

const useFeeSelectedDesktop = ({
  isPrimeMember,
  isPrimeAdded,
  wasPrimePriceSelected,
  wasActionTaken,
}): UseFeeSelected => {
  let feeSelected: FeeSelected;
  const primeAddedPrice = isPrimeAdded
    ? FeeSelected.PrimePrice
    : FeeSelected.FullPrice;
  const primeSelectedInResultsPrice = wasPrimePriceSelected
    ? FeeSelected.PrimePrice
    : FeeSelected.FullPrice;
  if (isPrimeMember || (isPrimeAdded && !wasPrimePriceSelected)) {
    feeSelected = FeeSelected.PrimePrice;
  } else {
    feeSelected = wasActionTaken
      ? primeAddedPrice
      : primeSelectedInResultsPrice;
  }
  return {
    feeSelected,
  };
};
